import React, { useState, useEffect } from "react";
import axios from "../../api/axiosInstance";
import { FaEdit } from "react-icons/fa";
import toast from "react-hot-toast";
import EditPreferenceModal from "./EditPreferenceModal";
import SelectPreferenceModal from "./SelectPreferenceModal";

const PreferenceItem = ({ label, value, onEdit }) => (
  <div className="border-b py-3 flex justify-between items-center">
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-md font-semibold text-gray-800 whitespace-pre-wrap">
        {value || "Not specified"}
      </p>
    </div>
    <button onClick={onEdit} className="text-gray-500 hover:text-pink-600">
      <FaEdit />
    </button>
  </div>
);

const UserPreferences = () => {
  const [preferences, setPreferences] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalField, setModalField] = useState("");
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const res = await axios.get("/api/preferences/me/", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      setPreferences(res.data);
    } catch {
      toast.error("Failed to load preferences");
    }
  };

  const handleModalSubmit = async (newValue) => {
    try {
      const payload = { user: user.id, [modalField]: newValue };
      await axios.patch("/api/preferences/me/", payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      toast.success(`${modalField.replace("_", " ")} updated`);
      fetchPreferences();
      setModalOpen(false);
    } catch {
      toast.error("Failed to update preference");
    }
  };

  const selectOptionsMap = {
    marital_status: ["Never Married", "Divorced", "Widowed"],
    mother_tongue: [
      "Hindi",
      "English",
      "Gujarati",
      "Marathi",
      "Bengali",
      "Punjabi",
      "Tamil",
      "Telugu",
    ],
    physical_status: ["Normal", "Physically Challenged"],
    diet: ["Vegetarian", "Non-Vegetarian", "Eggetarian", "Vegan"],
    religion: [
      "Hindu",
      "Muslim",
      "Christian",
      "Sikh",
      "Jain",
      "Buddhist",
      "Other",
    ],
    caste: [
      "Brahmin",
      "Rajput",
      "Kayastha",
      "Vaishya",
      "Jat",
      "SC",
      "ST",
      "OBC",
      "Other",
    ],
    gothra: ["Kashyap", "Bharadwaj", "Vashishtha", "Gautam", "Other"],
    country: ["India", "USA", "UK", "Canada", "Australia", "Other"],
    state: [
      "Madhya Pradesh",
      "Maharashtra",
      "Gujarat",
      "Delhi",
      "Karnataka",
      "Tamil Nadu",
      "Other",
    ],
    city: [
      "Indore",
      "Bhopal",
      "Mumbai",
      "Delhi",
      "Bengaluru",
      "Chennai",
      "Other",
    ],
    education: ["High School", "Bachelor's", "Master's", "PhD", "Other"],
    
  };

  const sections = [
   
    {
      title: "👤 Personal Preferences",
      fields: [
        "marital_status",
        "mother_tongue",
        "religion",
        "caste",
        "gothra",
        "education",
      ],
    },
    {
      title: "📍 Location Preferences",
      fields: ["country", "state", "city"],
    },
   
  ];

  const selectFields = Object.keys(selectOptionsMap);

  return (
    <div className="w-full max-w-screen-xl mx-auto p-9 bg-white rounded shadow">
      <h2 className="text-2xl font-bold text-pink-600 mb-6 flex items-center gap-2">
        Manage Your Choice 
      </h2>

      {/* AGE */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          📅 Age Preference
        </h3>
        <PreferenceItem
          label="Preferred Age"
          value={
            preferences.min_age && preferences.max_age
              ? `${preferences.min_age} - ${preferences.max_age} years`
              : ""
          }
          onEdit={() => {
            setModalField("age");
            setModalOpen(true);
          }}
        />
      </div>

      {/* HEIGHT */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          📏 Height Preference
        </h3>
        <PreferenceItem
          label="Preferred Height"
          value={
            preferences.min_height_feet && preferences.max_height_feet
              ? `${preferences.min_height_feet} Ft ${
                  preferences.min_height_inches || 0
                } In - ${preferences.max_height_feet} Ft ${
                  preferences.max_height_inches || 0
                } In`
              : ""
          }
          onEdit={() => {
            setModalField("height");
            setModalOpen(true);
          }}
        />
      </div>

      {/* SECTION WISE PREFERENCES */}
      {sections.map((section) => (
        <div key={section.title} className="mb-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            {section.title}
          </h3>
          {section.fields.map((field) => (
            <PreferenceItem
              key={field}
              label={field
                .replace("_", " ")
                .replace(/\b\w/g, (c) => c.toUpperCase())}
              value={preferences[field]}
              onEdit={() => {
                setModalField(field);
                setModalOpen(true);
              }}
            />
          ))}
        </div>
      ))}

      {/* MODALS */}
      <EditPreferenceModal
        title="Preferred Age"
        fromLabel="From Age"
        toLabel="To Age"
        fromValue={preferences.min_age}
        toValue={preferences.max_age}
        compulsoryValue={preferences.age_strict}
        isOpen={modalOpen && modalField === "age"}
        onClose={() => setModalOpen(false)}
        onSubmit={async ({ from, to, compulsory }) => {
          try {
            const payload = {
              user: user.id,
              min_age: from,
              max_age: to,
              age_strict: compulsory,
            };
            await axios.patch("/api/preferences/me/", payload, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "accessToken"
                )}`,
              },
            });
            toast.success("Age preference updated");
            fetchPreferences();
            setModalOpen(false);
          } catch {
            toast.error("Failed to update age preference");
          }
        }}
      />

      <EditPreferenceModal
        title="Preferred Height"
        fromLabel="From (e.g., 5.4)"
        toLabel="To (e.g., 6.0)"
        fromValue={
          preferences.min_height_feet
            ? `${preferences.min_height_feet}.${
                preferences.min_height_inches || 0
              }`
            : ""
        }
        toValue={
          preferences.max_height_feet
            ? `${preferences.max_height_feet}.${
                preferences.max_height_inches || 0
              }`
            : ""
        }
        compulsoryValue={preferences.height_strict}
        isOpen={modalOpen && modalField === "height"}
        onClose={() => setModalOpen(false)}
        onSubmit={async ({ from, to, compulsory }) => {
          try {
            const [fromFt, fromIn] = from.split(".").map((s) => s.trim());
            const [toFt, toIn] = to.split(".").map((s) => s.trim());
            const payload = {
              user: user.id,
              min_height_feet: fromFt,
              min_height_inches: fromIn || 0,
              max_height_feet: toFt,
              max_height_inches: toIn || 0,
              height_strict: compulsory,
            };
            await axios.patch("/api/preferences/me/", payload, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "accessToken"
                )}`,
              },
            });
            toast.success("Height preference updated");
            fetchPreferences();
            setModalOpen(false);
          } catch {
            toast.error("Failed to update height preference");
          }
        }}
      />

      <SelectPreferenceModal
        title={modalField
          .replace("_", " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())}
        options={selectOptionsMap[modalField] || []}
        value={preferences[modalField]}
        isOpen={modalOpen && selectFields.includes(modalField)}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
      />
    </div>
  );
};

export default UserPreferences;
